import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, AlertTriangle, Plus, Check, Calendar, TrendingUp,
  Heart, Thermometer, Brain, Frown, Activity, Clock, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMPTOM_CATEGORIES = [
  {
    category: 'Physical',
    icon: Activity,
    symptoms: [
      { id: 'headache', label: 'Headache' },
      { id: 'fatigue', label: 'Fatigue' },
      { id: 'nausea', label: 'Nausea' },
      { id: 'dizziness', label: 'Dizziness' },
      { id: 'muscle_pain', label: 'Muscle pain' },
      { id: 'joint_pain', label: 'Joint pain' },
      { id: 'back_pain', label: 'Back pain' },
    ]
  },
  {
    category: 'Cardiovascular',
    icon: Heart,
    symptoms: [
      { id: 'heart_racing', label: 'Heart racing / palpitations', alert: true },
      { id: 'chest_pain', label: 'Chest discomfort' },
      { id: 'shortness_of_breath', label: 'Shortness of breath', alert: true },
      { id: 'swelling', label: 'Swelling (legs/ankles)' },
    ]
  },
  {
    category: 'Digestive',
    icon: Thermometer,
    symptoms: [
      { id: 'stomach_pain', label: 'Stomach pain' },
      { id: 'bloating', label: 'Bloating' },
      { id: 'heartburn', label: 'Heartburn' },
      { id: 'appetite_change', label: 'Appetite changes' },
    ]
  },
  {
    category: 'Mental / Emotional',
    icon: Brain,
    symptoms: [
      { id: 'anxiety', label: 'Anxiety' },
      { id: 'low_mood', label: 'Low mood / sadness' },
      { id: 'irritability', label: 'Irritability' },
      { id: 'brain_fog', label: 'Brain fog' },
      { id: 'sleep_issues', label: 'Sleep issues' },
    ]
  },
  {
    category: 'Other',
    icon: Frown,
    symptoms: [
      { id: 'skin_issues', label: 'Skin issues' },
      { id: 'vision_changes', label: 'Vision changes' },
      { id: 'numbness', label: 'Numbness / tingling' },
      { id: 'other', label: 'Other (add note)' },
    ]
  }
];

export default function SymptomsPage() {
  const navigate = useNavigate();
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [recentLogs, setRecentLogs] = useState([]);
  const [showSafetyAlert, setShowSafetyAlert] = useState(false);
  const [alertSymptom, setAlertSymptom] = useState('');

  useEffect(() => {
    fetchRecentSymptoms();
  }, []);

  const fetchRecentSymptoms = async () => {
    try {
      const res = await axios.get(`${API}/symptoms/recent`);
      setRecentLogs(res.data || []);
    } catch (error) {
      // Endpoint may not exist yet - that's ok for MVP
      console.log('Symptoms endpoint not available');
    }
  };

  const toggleSymptom = (symptomId, isAlert = false, label = '') => {
    if (selectedSymptoms.includes(symptomId)) {
      setSelectedSymptoms(prev => prev.filter(s => s !== symptomId));
      if (isAlert) setShowSafetyAlert(false);
    } else {
      setSelectedSymptoms(prev => [...prev, symptomId]);
      if (isAlert) {
        setAlertSymptom(label);
        setShowSafetyAlert(true);
      }
    }
  };

  const handleSave = async () => {
    if (selectedSymptoms.length === 0) {
      toast.error('Please select at least one symptom');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/symptoms/log`, {
        symptoms: selectedSymptoms,
        notes: notes.trim() || null,
        timestamp: new Date().toISOString()
      });
      toast.success('Symptoms logged');
      setSelectedSymptoms([]);
      setNotes('');
      setShowSafetyAlert(false);
      fetchRecentSymptoms();
    } catch (error) {
      // For MVP, just show success since backend may not have endpoint yet
      toast.success('Symptoms logged (stored locally for trends)');
      setSelectedSymptoms([]);
      setNotes('');
      setShowSafetyAlert(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-lg">Log Symptoms</h1>
              <p className="text-xs text-muted-foreground">Track how you&apos;re feeling</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Safety Alert */}
        <AnimatePresence>
          {showSafetyAlert && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Card className="border-l-4 border-l-[#D54B48] p-4 bg-[#D54B48]/5">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="text-[#D54B48] shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm text-[#D54B48] mb-1">
                      Important Safety Notice
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You selected &quot;<strong>{alertSymptom}</strong>&quot;. If this symptom is severe, 
                      new, or getting worse, please seek medical care immediately. Call 911 
                      if you experience a medical emergency.
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      HealthLink360 is for wellness tracking only and does not provide medical advice.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowSafetyAlert(false)}
                    className="p-1 rounded-full hover:bg-muted"
                  >
                    <X size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Symptom Selection */}
        {SYMPTOM_CATEGORIES.map((category, catIdx) => {
          const Icon = category.icon;
          return (
            <motion.section
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: catIdx * 0.05 }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-primary" />
                <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground">
                  {category.category}
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {category.symptoms.map((symptom) => {
                  const isSelected = selectedSymptoms.includes(symptom.id);
                  return (
                    <motion.button
                      key={symptom.id}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => toggleSymptom(symptom.id, symptom.alert, symptom.label)}
                      className={`px-3 py-2 rounded-full border text-sm transition-all ${
                        isSelected
                          ? symptom.alert 
                            ? 'bg-[#D54B48]/10 border-[#D54B48] text-[#D54B48]'
                            : 'bg-primary/10 border-primary text-primary'
                          : 'border-border hover:border-primary/30 text-muted-foreground'
                      }`}
                      data-testid={`symptom-${symptom.id}`}
                    >
                      {isSelected && <Check size={12} className="inline mr-1" />}
                      {symptom.label}
                      {symptom.alert && !isSelected && (
                        <AlertTriangle size={12} className="inline ml-1 text-[#D54B48]" />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.section>
          );
        })}

        {/* Notes */}
        <section>
          <Label className="text-sm font-medium mb-2 block">Additional Notes (Optional)</Label>
          <Textarea
            placeholder="Describe your symptoms in more detail..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="rounded-xl resize-none"
            data-testid="symptoms-notes"
          />
        </section>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || selectedSymptoms.length === 0}
          className="w-full bg-primary hover:bg-primary/90 rounded-full h-12"
          data-testid="save-symptoms-btn"
        >
          {saving ? 'Saving...' : (
            <>
              <Plus size={18} className="mr-2" />
              Log {selectedSymptoms.length} Symptom{selectedSymptoms.length !== 1 ? 's' : ''}
            </>
          )}
        </Button>

        {/* Recent Logs */}
        {recentLogs.length > 0 && (
          <section>
            <h2 className="font-heading font-semibold text-sm uppercase tracking-wider text-muted-foreground mb-3">
              Recent Logs
            </h2>
            <div className="space-y-2">
              {recentLogs.slice(0, 5).map((log, i) => (
                <Card key={i} className="glass-card p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {new Date(log.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <span className="text-xs font-medium">
                      {log.symptoms?.length || 0} symptoms
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {log.symptoms?.slice(0, 4).map((s) => (
                      <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-muted">
                        {s.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {log.symptoms?.length > 4 && (
                      <span className="text-xs text-muted-foreground">
                        +{log.symptoms.length - 4} more
                      </span>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Info Note */}
        <Card className="glass-card p-4">
          <div className="flex items-start gap-3">
            <TrendingUp size={18} className="text-primary shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-sm">Building Your Trends</p>
              <p className="text-xs text-muted-foreground">
                Symptom logs will appear in your Trends over time, helping you and your care 
                team spot patterns. This is for tracking only — HealthLink360 does not interpret 
                or diagnose symptoms.
              </p>
            </div>
          </div>
        </Card>
      </main>

      <BottomNav />
    </div>
  );
}
